import React from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const HELP_CONTENT = `
# GSTU Needle – Benutzerhandbuch

## Was ist GSTU Needle?

GSTU Needle ist ein KI-gestütztes Werkzeug zur halbautomatischen Aktualisierung von Softwaredokumentationen. Es liest Use-Case-Änderungsanfragen aus Confluence und schlägt automatisch vor, welche Textstellen in den zugehörigen Handbüchern angepasst werden müssen. Die Redakteurin bzw. der Redakteur prüft jeden Vorschlag, bestätigt oder verwirft ihn und lädt anschließend das fertige DOCX-Handbuch herunter.

---

## Voraussetzungen

Bevor Sie mit einer neuen Verarbeitung beginnen, müssen zwei Dinge vorbereitet sein:

1. **Confluence-Seiten** mit den Use Cases (siehe Abschnitt Confluence-Struktur unten)
2. **DOCX-Handbücher** in der richtigen Benennung (siehe Abschnitt Handbuch-Benennung unten)

---

## Confluence-Struktur

### Übergeordnete Seite (Parent Page)

Alle Use Cases einer Änderungsrunde müssen als **Unterseiten einer gemeinsamen Confluence-Seite** angelegt sein. Die numerische ID dieser Parent Page geben Sie beim Start in das Feld **„Confluence Parent ID"** ein.

### Benennung der Use-Case-Seiten

Jede Use-Case-Seite **muss mit dem Wort \`Use Case\` beginnen**. Seiten mit anderem Titel werden ignoriert.

### Primäre Akteure – Zuweisung zum Handbuch

Jede Use-Case-Seite muss einen Abschnitt **„Primäre Akteure"** enthalten. Das System ermittelt daraus, zu welchem Handbuch die Änderungen gehören:

| Schlüsselwort in „Primäre Akteure" | Handbuch |
|---|---|
| \`Betreibende\` / \`betreibend\` | Betreibende-Handbuch |
| \`Unterkunft\` / \`Unterkunftsmanagement\` | UntM-Handbuch |
| \`Zuweisend\` / \`Zuweisende Stelle\` | ZuwSt-Handbuch |
| \`Admin\` | Admin-Handbuch |

---

## Handbuch-Benennung

DOCX-Dateinamen müssen mit einem dieser Präfixe beginnen:

| Präfix | Handbuch |
|---|---|
| \`Betreibende\` | z. B. \`Betreibende_HB_V2.3.0_final.docx\` |
| \`UntM\` | z. B. \`UntM_HB_V2.3.0_final.docx\` |
| \`ZuwSt\` | z. B. \`ZuwSt_HB_V2.3.0_final.docx\` |
| \`Admin\` | z. B. \`Admin_HB_V2.3.0_final.docx\` |

---

## Schritt-für-Schritt-Anleitung

### Schritt 1 – Sitzung starten

1. Geben Sie die numerische Confluence-ID der Parent Page ein.
2. Klicken Sie auf **„+ Handbücher auswählen"** und wählen Sie die DOCX-Dateien aus.
3. Klicken Sie auf **„Use Cases Laden"**.

Gespeicherte Sitzungen werden unterhalb aufgelistet – ein Klick darauf setzt die Arbeit fort.

### Schritt 2 – Use Case verarbeiten

Klicken Sie auf **„Use Case Verarbeiten"**, um die KI-Analyse für den angezeigten Use Case zu starten. Die KI vergleicht den Use Case mit dem Handbuch und schlägt Textstellen vor, die angepasst werden müssen.

### Schritt 3 – Vorschläge prüfen

Für jeden Vorschlag sehen Sie:

| Feld | Bedeutung |
|---|---|
| **Kategorie-Tag** | Priorität (Kritisch / Neu / Unterstützend / Verbesserung) |
| **Abschnittsstandort** | Kapitel im Handbuch |
| **Context** | Warum dieser Abschnitt betroffen ist |
| **Reasoning** | Begründung der Textänderung |
| **Original Text** | Aktueller Text im Handbuch |
| **Updated Text** | KI-Vorschlag (editierbar) |

**Aktionen pro Karte:**
- **„Im Kontext anzeigen"** – öffnet das Handbuch mit hervorgehobenem Fundort
- **Updated Text bearbeiten** – Vorschlag direkt im Textfeld anpassen
- **„✓ Bestätigen"** – Änderung ins Handbuch übernehmen
- **„✕ Ablehnen"** – Vorschlag verwerfen
- **„Bearbeiten"** – bestätigte Änderung nachträglich korrigieren

### Schritt 4 – Navigation

Vorschläge der Kategorien **Kritisch**, **Neu** und **Unterstützend** müssen bestätigt oder abgelehnt werden, bevor Sie zum nächsten Use Case weiternavigieren können. **Verbesserung** ist optional.

### Schritt 5 – Handbuch herunterladen

Klicken Sie auf **„↓ Zwischenergebnis herunterladen"**, um das Handbuch als DOCX mit allen bisher bestätigten Änderungen zu exportieren.

---

## Häufige Fehler

| Problem | Lösung |
|---|---|
| „Confluence page not found" | Numerische ID der Parent Page prüfen |
| „Access forbidden" | Confluence-Berechtigungen prüfen |
| Handbuch-Upload schlägt fehl | Dateiname muss mit \`Betreibende\`, \`UntM\`, \`ZuwSt\` oder \`Admin\` beginnen |
| Use Case erscheint nicht | Seitentitel muss mit „Use Case" beginnen |
| Falsche Handbuch-Gruppe | Abschnitt „Primäre Akteure" in Confluence prüfen |
| Navigation gesperrt | Alle Pflicht-Vorschläge (Kritisch / Neu / Unterstützend) bestätigen oder ablehnen |
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 500;
`;

const Panel = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 780px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.75rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #111;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  border-radius: 4px;

  &:hover {
    color: #111;
    background: #f3f4f6;
  }
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 1.75rem 2rem;
  font-size: 0.9rem;
  line-height: 1.7;
  color: #374151;

  h1 {
    font-size: 1.4rem;
    font-weight: 700;
    color: #111;
    margin: 0 0 0.5rem;
  }

  h2 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #111;
    margin: 2rem 0 0.5rem;
    padding-bottom: 0.35rem;
    border-bottom: 1px solid #e5e7eb;
  }

  h3 {
    font-size: 0.95rem;
    font-weight: 600;
    color: #374151;
    margin: 1.25rem 0 0.35rem;
  }

  p {
    margin: 0.5rem 0;
  }

  ul, ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin: 0.25rem 0;
  }

  code {
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 0.1em 0.4em;
    font-size: 0.85em;
    font-family: 'DM Mono', 'Menlo', 'Monaco', monospace;
    color: #003A70;
  }

  hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 1.5rem 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.75rem 0;
    font-size: 0.875rem;
  }

  th {
    background: #f9fafb;
    font-weight: 600;
    color: #111;
    text-align: left;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e5e7eb;
  }

  td {
    padding: 0.5rem 0.75rem;
    border: 1px solid #e5e7eb;
    vertical-align: top;
  }

  tr:nth-child(even) td {
    background: #fafafa;
  }

  strong {
    color: #111;
    font-weight: 600;
  }
`;

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Panel>
        <PanelHeader>
          <PanelTitle>Benutzerhandbuch</PanelTitle>
          <CloseButton onClick={onClose} aria-label="Schließen">&times;</CloseButton>
        </PanelHeader>
        <Body>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {HELP_CONTENT}
          </ReactMarkdown>
        </Body>
      </Panel>
    </Overlay>
  );
};

export default HelpModal;
